import { useEffect, useRef, useState } from "react";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import Select from "../../../../components/input/Select";
import { SimpleOption } from "../../../../components/input/SimpleListbox";
import { tooltipMark, formatNumberOneDecimal } from "../../shared/reportUtil";
import { ReportLevel } from "../adminReportsApiSlice";
import { traitChartColors } from "../adminReportsUtil";

const seriesTypes = [
	"Development of Ideas",
	"Organization",
	"Style",
	"Word Choice",
	"Sentence Fluency",
	"Conventions",
	"Total Score",
];

const seriesOptions: SimpleOption[] = [
	{ value: "all", label: "All Traits" },
	{ value: seriesTypes[0], label: seriesTypes[0] },
	{ value: seriesTypes[1], label: seriesTypes[1] },
	{ value: seriesTypes[2], label: seriesTypes[2] },
	{ value: seriesTypes[3], label: seriesTypes[3] },
	{ value: seriesTypes[4], label: seriesTypes[4] },
	{ value: seriesTypes[5], label: seriesTypes[5] },
	{ value: seriesTypes[6], label: seriesTypes[6] },
];

// Required properties for the chart
interface TraitScoresRequiredBase {
	averageDevelopment: number;
	averageOrganization: number;
	averageStyle: number;
	averageWordChoice: number;
	averageSentenceStructure: number;
	averageConventions: number;
}

interface TraitScoresChartProps<T extends TraitScoresRequiredBase> {
	level: ReportLevel;
	data: T[];
	hasData: boolean;
	title: string;
	yAxisDataField: keyof T;
	totalScoreField: keyof T; // Property name for total score
	gridLeft?: number;
}

export function TraitScoresChart<T extends TraitScoresRequiredBase>(
	props: TraitScoresChartProps<T>
) {
	const [focusedSeries, setFocusedSeries] = useState("all");

	const chartRef = useRef<ReactEcharts>(null);
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	let chartColors = chartPalette;
	if (!chartPalette) {
		chartColors = traitChartColors;
	}

	const getDisplayedSeries = () => {
		if (focusedSeries === "all") {
			return {
				[seriesTypes[0]]: true,
				[seriesTypes[1]]: true,
				[seriesTypes[2]]: true,
				[seriesTypes[3]]: true,
				[seriesTypes[4]]: true,
				[seriesTypes[5]]: true,
				[seriesTypes[6]]: true,
			};
		}

		// Only set the selected series from the dropdown to true, hide the rest
		return {
			[seriesTypes[0]]: focusedSeries === seriesTypes[0],
			[seriesTypes[1]]: focusedSeries === seriesTypes[1],
			[seriesTypes[2]]: focusedSeries === seriesTypes[2],
			[seriesTypes[3]]: focusedSeries === seriesTypes[3],
			[seriesTypes[4]]: focusedSeries === seriesTypes[4],
			[seriesTypes[5]]: focusedSeries === seriesTypes[5],
			[seriesTypes[6]]: focusedSeries === seriesTypes[6],
		};
	};

	const initialEChartOptions: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		title: {
			text: props.title,
			left: "center",
			textStyle: { fontSize: 16, fontWeight: 600, color: foregroundColor },
		},
		legend: {
			selected: getDisplayedSeries(),
			selectedMode: false,
			top: 35,
			textStyle: {
				color: foregroundColor,
			},
		},
		grid: {
			containLabel: true,
			left: props.gridLeft ?? 20,
			right: 40,
			bottom: 30,
			top: 75,
		},
		xAxis: {
			type: "value",
			show: true,
			name: "Average Score",
			nameLocation: "middle",
			nameGap: 30,
			min: 0,
			minInterval: 1,
			max: focusedSeries === seriesTypes[6] || focusedSeries === "all" ? 30 : 5,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
					width: 1,
				},
			},
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		yAxis: {
			type: "category",
			data: props.data.map((row) => row[props.yAxisDataField]),
			inverse: true, // Forces the order of the rows in the table on initial load
			axisPointer: { type: "shadow" },
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			axisLabel: {
				padding: 10,
				lineHeight: 15,
				interval: 0,
				width: 180,
				overflow: "break",
				hideOverlap: false,
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: {
					axisValue: string;
					dataIndex: number;
					color: string;
					seriesName: string;
					value: string;
				}[]
			) => {
				if (!params.find((p) => p.value)) return;

				const label = `<div>${props.data[params[0].dataIndex][props.yAxisDataField]}</div>`;
				const lineItem = (p: any) => {
					return `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${formatNumberOneDecimal(
						p.seriesName === seriesTypes[6]
							? (props.data[p.dataIndex][props.totalScoreField] as number)
							: p.value
					)}</b></div>`;
				};
				const content = params.reduce((a: string, v: any) => a + lineItem(v), label);

				return content;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [],
	};

	useEffect(() => {
		const options = updateOptions(
			initialEChartOptions,
			buildSeriesOptions(
				props.data,
				focusedSeries === seriesTypes[6],
				foregroundColor,
				chartColors,
				props.totalScoreField
			)
		);

		if (chartPalette != undefined) {
			options.color = chartPalette;
		}

		chartRef.current?.getEchartsInstance().setOption(options, true);
	}, [props.data, chartPalette, foregroundColor, accentColor, focusedSeries]);

	const chartHeight = props.data.length * 40 + 150;

	return (
		<>
			{props.hasData && (
				<div className="relative w-full mt-4 p-4 border-2 border-gray-200">
					<div className="z-10 absolute top-4 left-4">
						<Select
							simpleListBoxId="series-filter"
							value={focusedSeries}
							setValue={(value) => setFocusedSeries(value as string)}
							options={seriesOptions}
							fieldClasses="flex items-center gap-3"
							simpleListBoxClasses="w-64"
						/>
					</div>

					<ReactEcharts
						ref={chartRef}
						option={initialEChartOptions}
						className="w-full !min-h-96"
						style={{
							height: chartHeight,
						}}
					/>
				</div>
			)}
		</>
	);
}

function buildSeriesOptions<T extends TraitScoresRequiredBase>(
	data: T[],
	isTotalOnly: boolean,
	foregroundColor: string,
	chartColors: string[],
	totalScoreField: keyof T
): EChartsOption["series"] {
	function makeSeries(data: number[], index: number, isTotal: boolean = false) {
		return {
			name: seriesTypes[index],
			type: "bar",
			cursor: "default",
			stack: "total",
			data: isTotal ? data.map(() => 0) : data,
			itemStyle: { color: chartColors[index] },
			barMinWidth: 15,
			emphasis: {
				itemStyle: {
					shadowBlur: 7,
				},
			},
			label: {
				show: true,
				fontWeight: "bold",
				color: foregroundColor,
				formatter: (params: any) => {
					return formatNumberOneDecimal(isTotal ? data[params.dataIndex] : params.value);
				},
				position: isTotal ? "right" : "inside",
			},
		};
	}

	return isTotalOnly
		? makeSeries(
				data.map((row) => row[totalScoreField] as number),
				6
			)
		: [
				makeSeries(
					data.map((row) => row.averageDevelopment),
					0
				),
				makeSeries(
					data.map((row) => row.averageOrganization),
					1
				),
				makeSeries(
					data.map((row) => row.averageStyle),
					2
				),
				makeSeries(
					data.map((row) => row.averageWordChoice),
					3
				),
				makeSeries(
					data.map((row) => row.averageSentenceStructure),
					4
				),
				makeSeries(
					data.map((row) => row.averageConventions),
					5
				),
				makeSeries(
					data.map((row) => row[totalScoreField] as number),
					6,
					true
				),
			];
}

function updateOptions(options: EChartsOption, seriesData: EChartsOption["series"]): EChartsOption {
	options.series = seriesData;
	return options;
}
